import { useRef, useEffect } from "react";
import { Avatar, Box, Skeleton, InputBase, IconButton } from "@mui/material";
import { useTranslation } from "react-i18next";

import { MakeCommentFieldProps } from "../../../../types/components/props.type";
import { useProfileData } from "../../../../../../hooks/useProfileData";
import { useCommentFormatter } from "../../../../hooks/useCommentFormatter";
import { useCreateCommentMutation } from "../../../../api/mutations/useCreateCommentMutation";

import { useMentions } from "../../../../hooks/useMentions";

import UsersMentionList from "./UsersMentionList";
import { CommentInput } from "./CommentInput";
import { SendCommentButtonIcon } from "../../../../assets/icons/post-icons";

const MakeCommentField = ({ postId, parentComment }: MakeCommentFieldProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const hiddenInputRef = useRef<HTMLInputElement>(null);

  const { t } = useTranslation("feed", { keyPrefix: "comment" });
  const { data: userData, isLoading: isUserLoading } = useProfileData();
  const { firstTwoChr, media } = userData;

  const {
    mentions,
    showMentions,
    users,
    isSearchLoading,
    handleMentionInput,
    handleUserSelect,
    verifyMentions,
    updateHiddenInput,
    resetMentions,
  } = useMentions({ contentRef, hiddenInputRef });

  const { createComment, createReply } = useCreateCommentMutation({ postId });

  const formattedComment = useCommentFormatter(parentComment);
  const { authorFullName: parentCommentAuthorFullName } = formattedComment || {
    authorFullName: null,
  };

  // Update hidden input whenever mentions change or text content changes
  const handleContentChange = (event: React.FormEvent<HTMLDivElement>) => {
    handleMentionInput();
    updateHiddenInput();
  };

  const handleSubmit = () => {
    if (!contentRef.current || !hiddenInputRef.current) return;

    verifyMentions();
    updateHiddenInput();

    const finalText = hiddenInputRef.current.value.trim();
    if (!finalText) return;

    if (parentComment) {
      createReply({ commentId: parentComment.id, text: finalText });
    } else if (postId) {
      createComment(finalText);
    }

    // Reset state
    if (contentRef.current) {
      contentRef.current.textContent = "";
    }
    resetMentions();

    // Ensure the contentRef is focused after clearing
    contentRef.current?.focus();
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSubmit();
    } else {
      // Update hidden input on any key press
      updateHiddenInput();
    }
  };

  const handleSendComment = () => {
    handleSubmit();
  };
  const placeholder = parentComment ? t("replyTo", { name: parentCommentAuthorFullName }) : t("makeComment");

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <Avatar sx={{ width: "40px", height: "40px" }} src={isUserLoading ? undefined : media}>
          {isUserLoading ? <Skeleton variant="circular" animation="wave" /> : firstTwoChr}
        </Avatar>
        <Box sx={{ width: "100%", position: "relative", background: "var(--Gray---50, #F5F6F5)" }}>
          <InputBase inputRef={hiddenInputRef} type="hidden" sx={{ display: "none" }} />

          <CommentInput
            contentRef={contentRef}
            placeholder={placeholder}
            onInput={handleContentChange}
            onKeyDown={handleKeyPress}
            onBlur={verifyMentions}
          />

          <UsersMentionList
            showMentions={showMentions}
            isSearchLoading={isSearchLoading}
            contentRef={contentRef}
            users={users}
            handleUserSelect={handleUserSelect}
          />

          <IconButton
            sx={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)" }}
            size="small"
            onClick={handleSendComment}
          >
            <SendCommentButtonIcon />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default MakeCommentField;
